
    // IMPORTS
    import { computed, defineComponent, ref, watch } from 'vue'
    import { useEquipmentStore } from '@/store/equipment'
    import { buildLocalization, EquipmentApi } from '@/util'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import LoaderGuard from '@/components/common/loader-guard.vue'

    import iconLightGreen from '@/img/icon-light-green.svg'
    import iconLightYellow from '@/img/icon-light-yellow.svg'
    import iconLightRed from '@/img/icon-light-red.svg'
    import iconDownload from '@/img/icon-download.svg'
    import { EquipmentMasterData } from '@/types/equipment'

    export default defineComponent({
        name: 'equipmentSoftware',

        components: {
            LoaderGuard,
            Card,
            CardHeader,
            CardTitle,
            CardBody
        },


        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const equipmentStore = useEquipmentStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const downloadUrl = ref<string>('')
            const publicData = { iconDownload, downloadUrl }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const masterData = computed(() => equipmentStore.masterData == null ? null : new EquipmentMasterData(equipmentStore.masterData))
            const hasLoaded = computed(() => masterData.value != null)
            const software = computed(() => masterData.value?.software)
            const installedVersion = computed(() => masterData.value?.installedVersion)
            const availableVersion = computed(() => masterData.value?.availableVersion)
            const availableBuild = computed(() => masterData.value?.availableBuild)
            const isUpToDate = computed(() => masterData.value?.isUpToDate === true)

            const statusTitle = computed(() => {
                if (isUpToDate.value) {
                    return localize('LatestVersionInstalled')
                }
                if (masterData.value?.isMinorUpgrade) {
                    return localize('MinorUpgradeAvailableTitle')
                }
                if (masterData.value?.isMajorUpgrade) {
                    return localize('ImportantUpgradeAvailableTitle')
                }

                return ''
            })

            const statusDescription = computed(() => {
                if (masterData.value?.isMinorUpgrade) {
                    return localize('MinorUpgradeAvailableDescription')
                }
                if (masterData.value?.isMajorUpgrade) {
                    return localize('ImportantUpgradeAvailableDescription')
                }

                return ''
            })

            const statusIcon = computed(() => {
                if (isUpToDate.value) {
                    return iconLightGreen
                }
                if (masterData.value?.isMinorUpgrade) {
                    return iconLightYellow
                }
                if (masterData.value?.isMajorUpgrade) {
                    return iconLightRed
                }
                return null
            })

            const statusStyle = computed(() => {
                if (isUpToDate.value) {
                    return "color: green"
                }
                if (masterData.value?.isMajorUpgrade) {
                    return "color: red"
                }
                return "color: black"
            })

            const showUpdate = computed(() => !isUpToDate.value && availableBuild.value != null)

            const computedData = {
                masterData,
                hasLoaded,
                software,
                installedVersion,
                availableVersion,
                isUpToDate,
                statusTitle,
                statusDescription,
                statusIcon,
                statusStyle,
                showUpdate
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function getDownloadUrl(buildNumber: number) {
                const equipmentApi = new EquipmentApi
                equipmentApi.getDownloadUrl(buildNumber, masterData.value?.serialNo ?? '')
                    .then(response => downloadUrl.value = response.data)
            }


            const publicMethods = { localize }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            watch(availableBuild, (buildNumber) => {
                if (buildNumber != null) {
                    getDownloadUrl(buildNumber)
                }
                else {
                    downloadUrl.value = ''
                }
            }, { immediate: true })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    })
